import { useState, useEffect, useCallback } from 'react';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, MONO, BASE } from '../lib/constants';
import { Horizon, Btn, Mo, Bd, Dp, Label, Ic, dIc } from './shared';
import { updateDebt, deleteDebt } from '../lib/api';
import PayerCodeLinker from './PayerCodeLinker';
import PaymentSourceLinkModal from './PaymentSourceLinkModal';

const API = process.env.REACT_APP_BACKEND_URL;

const ACCOUNT_TYPES = ['credit', 'auto', 'personal', 'student', 'mortgage', 'bnpl', 'lease', 'payday', 'title', 'mca', 'medical', 'collections', 'tax', 'loan401k'];

const INPUT_STYLE = {
  width: '100%', padding: '11px 14px', background: 'var(--faint)', border: '1px solid var(--ghost)',
  color: 'var(--light)', fontFamily: MONO, fontSize: 13, outline: 'none', boxSizing: 'border-box',
  borderRadius: 8, marginBottom: 4,
};

/* ══════════════════════════════════════
   EDIT MODAL
   ══════════════════════════════════════ */
function EditModal({ debt, onSave, onClose, saving }) {
  const [form, setForm] = useState({
    name: debt.name || '',
    mask: debt.mask || '',
    statement_name: debt.statement_name || '',
    balance: debt.balance ?? '',
    apr: debt.apr ?? '',
    min_payment: debt.min_payment ?? '',
    due: debt.due || '',
    original: debt.original ?? '',
    credit_limit: debt.credit_limit ?? '',
    type: debt.type || 'credit',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const fields = [
    { key: 'name', label: 'ACCOUNT NAME', type: 'text', ph: 'e.g. Chase Sapphire' },
    { key: 'mask', label: 'LAST 4 DIGITS', type: 'text', ph: 'e.g. 5154', maxLength: 4 },
    { key: 'statement_name', label: 'NAME ON BANK STATEMENT', type: 'text', ph: 'e.g. CITI AUTOPAY' },
    { key: 'balance', label: 'CURRENT BALANCE ($)', type: 'number', ph: '0.00' },
    { key: 'apr', label: 'APR (%)', type: 'number', ph: '0.00' },
    { key: 'min_payment', label: 'MINIMUM PAYMENT ($)', type: 'number', ph: '0.00' },
    { key: 'due', label: 'DUE DATE (DAY OF MONTH)', type: 'text', ph: 'e.g. 15' },
    { key: 'original', label: 'ORIGINAL AMOUNT ($)', type: 'number', ph: '0.00' },
    { key: 'credit_limit', label: 'CREDIT LIMIT ($)', type: 'number', ph: '0.00' },
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#0F0F13', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 520,
        maxHeight: '85vh', overflowY: 'auto', padding: '28px 20px 40px',
      }}>
        {/* Handle bar */}
        <div style={{ width: 36, height: 4, background: 'var(--ghost)', borderRadius: 2, margin: '0 auto 24px' }} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <Dp style={{ fontSize: 20, fontWeight: 800, color: 'var(--light)' }}>Edit Account</Dp>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}>
            {Ic.x(20, MUTE)}
          </button>
        </div>

        {fields.map(f => (
          <div key={f.key} style={{ marginBottom: 16 }}>
            <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 6 }}>{f.label}</Mo>
            <input
              type={f.type}
              placeholder={f.ph}
              value={form[f.key]}
              onChange={e => set(f.key, e.target.value)}
              step={f.type === 'number' ? '0.01' : undefined}
              min={f.type === 'number' ? '0' : undefined}
              maxLength={f.maxLength || undefined}
              style={INPUT_STYLE}
            />
            {f.key === 'statement_name' && (
              <Mo style={{ fontSize: 11, color: 'var(--mute)', marginTop: 4, lineHeight: 1.4 }}>
                Check your bank app — look for how this payment appears in your checking account.
              </Mo>
            )}
          </div>
        ))}

        {/* Account Type */}
        <div style={{ marginBottom: 24 }}>
          <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 6 }}>ACCOUNT TYPE</Mo>
          <select
            value={form.type}
            onChange={e => set('type', e.target.value)}
            style={{ ...INPUT_STYLE, cursor: 'pointer', colorScheme: 'dark', background: '#1A1F28', color: '#E8E6E3' }}
          >
            {ACCOUNT_TYPES.map(t => (
              <option key={t} value={t} style={{ background: '#1A1F28', color: '#E8E6E3' }}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <Btn secondary onClick={onClose} disabled={saving} style={{ flex: 1 }}>CANCEL</Btn>
          <Btn gold onClick={() => onSave(form)} disabled={saving} style={{ flex: 1 }}>
            {saving ? 'SAVING...' : 'SAVE CHANGES'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   DELETE CONFIRM MODAL
   ══════════════════════════════════════ */
function DeleteConfirmModal({ debtName, onConfirm, onClose, deleting }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#0F0F13', border: `1px solid ${EMBER}40`,
        borderRadius: 16, width: '100%', maxWidth: 360, padding: 28,
        boxShadow: `0 0 40px ${EMBER}20`,
      }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{
            width: 56, height: 56, background: `${EMBER}15`, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            {Ic.x(24, EMBER)}
          </div>
          <Dp style={{ fontSize: 18, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 10 }}>
            Remove Account?
          </Dp>
          <Bd style={{ fontSize: 13, color: 'var(--mute)', lineHeight: 1.6 }}>
            This will permanently remove <strong style={{ color: 'var(--light)' }}>{debtName}</strong> from your dashboard. This cannot be undone.
          </Bd>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <Btn secondary onClick={onClose} disabled={deleting} style={{ flex: 1 }}>CANCEL</Btn>
          <button
            onClick={onConfirm}
            disabled={deleting}
            style={{
              flex: 1, padding: '13px 16px', background: deleting ? 'var(--ghost)' : EMBER,
              border: 'none', borderRadius: 10, color: BASE, fontFamily: MONO,
              fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: deleting ? 'not-allowed' : 'pointer',
            }}
          >
            {deleting ? 'REMOVING...' : 'YES, REMOVE'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   OVERVIEW TAB
   ══════════════════════════════════════ */
function OverviewTab({ debt }) {
  const paidOff = debt.original - debt.balance;
  const pctPaid = debt.original > 0 ? Math.round((paidOff / debt.original) * 100) : 0;

  return (
    <div className="fade-in">
      {/* Balance Card */}
      <div style={{ padding: 20, background: 'var(--surface)', border: '1px solid var(--faint)', borderRadius: 12, marginBottom: 16 }}>
        <div className="dettio-flex dettio-flex--between dettio-flex--center" style={{ marginBottom: 16 }}>
          <div>
            <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 4 }}>CURRENT BALANCE</Mo>
            <Dp style={{ fontSize: 32, fontWeight: 800, color: 'var(--light)' }}>${debt.balance?.toLocaleString()}</Dp>
          </div>
          {debt.is_target && (
            <Mo style={{ fontSize: 9, fontWeight: 700, padding: '6px 10px', background: `${GOLD}15`, color: GOLD, letterSpacing: 1.5, borderRadius: 8 }}>TARGET</Mo>
          )}
        </div>
        
        {/* Progress bar */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ height: 6, background: 'var(--faint)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pctPaid}%`, background: `linear-gradient(90deg, ${EMBER}, ${GOLD})`, borderRadius: 3, transition: 'width 0.5s' }} />
          </div>
          <div className="dettio-flex dettio-flex--between" style={{ marginTop: 6 }}>
            <Mo style={{ fontSize: 10, color: GOLD }}>{pctPaid}% paid off</Mo>
            <Mo style={{ fontSize: 10, color: 'var(--mute)' }}>${paidOff.toLocaleString()} of ${debt.original?.toLocaleString()}</Mo>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="dettio-flex dettio-flex--gap-md" style={{ marginBottom: 16 }}>
        <div style={{ flex: 1, padding: 14, background: 'var(--surface)', border: '1px solid var(--faint)', borderRadius: 10, textAlign: 'center' }}>
          <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 4 }}>APR</Mo>
          <Dp style={{ fontSize: 20, fontWeight: 700, color: debt.apr > 20 ? EMBER : 'var(--light)' }}>{debt.apr || 0}%</Dp>
        </div>
        <div style={{ flex: 1, padding: 14, background: 'var(--surface)', border: '1px solid var(--faint)', borderRadius: 10, textAlign: 'center' }}>
          <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 4 }}>MIN PAYMENT</Mo>
          <Dp style={{ fontSize: 20, fontWeight: 700, color: 'var(--light)' }}>${debt.min_payment || 0}</Dp>
        </div>
        <div style={{ flex: 1, padding: 14, background: 'var(--surface)', border: '1px solid var(--faint)', borderRadius: 10, textAlign: 'center' }}>
          <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 4 }}>DUE DAY</Mo>
          <Dp style={{ fontSize: 20, fontWeight: 700, color: 'var(--light)' }}>{debt.due || '—'}</Dp>
        </div>
      </div>

      {/* Details */}
      <div style={{ padding: 16, background: 'var(--surface)', border: '1px solid var(--faint)', borderRadius: 12 }}>
        <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 12 }}>ACCOUNT DETAILS</Mo>
        {[
          { label: 'Type', value: debt.type?.charAt(0).toUpperCase() + debt.type?.slice(1) },
          { label: 'Original Amount', value: `$${debt.original?.toLocaleString() || 0}` },
          debt.credit_limit > 0 && { label: 'Credit Limit', value: `$${debt.credit_limit?.toLocaleString()}` },
          debt.loan_term > 0 && { label: 'Loan Term', value: `${debt.loan_term} months` },
          debt.auto_pay && { label: 'Auto-Pay', value: 'Enabled' },
        ].filter(Boolean).map((item, i) => (
          <div key={i} className="dettio-flex dettio-flex--between" style={{ padding: '10px 0', borderBottom: i < 4 ? '1px solid var(--faint)' : 'none' }}>
            <Bd style={{ fontSize: 13, color: 'var(--mute)' }}>{item.label}</Bd>
            <Bd style={{ fontSize: 13, fontWeight: 600, color: 'var(--light)' }}>{item.value}</Bd>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   PAYMENTS TAB - Auto-populated from Plaid
   ══════════════════════════════════════ */
function PaymentsTab({ debt }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsPaymentLink, setNeedsPaymentLink] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [justLinked, setJustLinked] = useState(false); // true after user confirms link in this session
  const [pendingAlerts, setPendingAlerts] = useState([]);
  const [actioning, setActioning] = useState(null); // alert_id being confirmed/dismissed

  // hasReferenceCode uses justLinked as an override so the stale debt prop doesn't keep the banner visible
  const hasReferenceCode = justLinked || !!(debt?.payment_source?.reference_code || debt?.payer_reference_code);

  const fetchAlerts = async () => {
    try {
      const res = await fetch(`${API}/api/payment-alerts/pending?debt_id=${debt.id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPendingAlerts(data.alerts || []);
      }
    } catch (e) { /* non-critical */ }
  };

  const handleAlertAction = async (alertId, action) => {
    setActioning(alertId + action);
    try {
      await fetch(`${API}/api/payment-alerts/${alertId}/${action}`, { method: 'POST', credentials: 'include' });
      setPendingAlerts(prev => prev.filter(a => a.id !== alertId));
      if (action === 'confirm') fetchPayments();
    } catch (e) { console.error(e); }
    setActioning(null);
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/debts/${debt.id}/plaid-payments?months=12`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.needs_payment_link) {
          setNeedsPaymentLink(true);
          setPayments([]);
        } else {
          setNeedsPaymentLink(false);
          setPayments(data.payments || []);
        }
      } else {
        setError('Failed to load payments');
      }
    } catch (err) {
      setError('Failed to load payments');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPayments();
    fetchAlerts();
  }, [debt.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="fade-in" style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ width: 32, height: 32, margin: '0 auto 16px' }}>
          <svg width={32} height={32} viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
            <circle cx="12" cy="12" r="10" fill="none" stroke="var(--faint)" strokeWidth="2" />
            <circle cx="12" cy="12" r="10" fill="none" stroke={GOLD} strokeWidth="2" strokeDasharray="30 70" strokeLinecap="round" />
          </svg>
        </div>
        <Mo style={{ fontSize: 12, color: 'var(--mute)' }}>Loading payment history...</Mo>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fade-in" style={{ textAlign: 'center', padding: 40 }}>
        <Bd style={{ fontSize: 13, color: 'var(--mute)' }}>{error}</Bd>
      </div>
    );
  }

  // Banner shown whenever debt has no confirmed reference code OR backend signals ambiguity
  const showLinkBanner = !hasReferenceCode || needsPaymentLink;

  return (
    <div className="fade-in">

      {/* ── PAYMENTS NOT CONNECTED banner ── */}
      {showLinkBanner && (
        <div style={{
          background: '#1a1400',
          border: '1px solid #3a2800',
          borderRadius: 12,
          padding: '14px 16px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}>
          <div>
            <Mo style={{ fontSize: 10, color: GOLD, letterSpacing: 1.5, display: 'block', marginBottom: 4 }}>
              PAYMENTS NOT CONNECTED
            </Mo>
            <Bd style={{ fontSize: 12, color: 'var(--mute)', lineHeight: 1.5 }}>
              {needsPaymentLink
                ? 'Multiple accounts from this issuer detected — link this specific account to see its payments.'
                : 'Link your bank statement to automatically track payments for this account.'}
            </Bd>
          </div>
          <button
            data-testid="link-payment-source-btn"
            onClick={() => setShowLinkModal(true)}
            style={{
              flexShrink: 0,
              background: 'transparent',
              border: `1px solid ${GOLD}40`,
              borderRadius: 8,
              padding: '8px 12px',
              cursor: 'pointer',
            }}
          >
            <Mo style={{ fontSize: 10, color: GOLD, letterSpacing: 1 }}>LINK →</Mo>
          </button>
        </div>
      )}

      {/* Payment Source Link Modal */}
      {showLinkModal && (
        <PaymentSourceLinkModal
          debt={debt}
          onClose={() => setShowLinkModal(false)}
          onLinked={(freshPayments) => {
            setShowLinkModal(false);
            setJustLinked(true);
            setNeedsPaymentLink(false);
            if (freshPayments?.length) {
              setPayments(freshPayments);
            } else {
              fetchPayments();
            }
          }}
        />
      )}

      {/* Payer Code Linker — auto-detects unmatched codes and prompts user to confirm */}
      <PayerCodeLinker debt={debt} onCodeLinked={fetchPayments} />

      {/* Unconfirmed payment alerts */}
      {pendingAlerts.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Mo style={{ fontSize: 10, color: 'var(--gold)', letterSpacing: 1.5, display: 'block', marginBottom: 10 }}>
            UNCONFIRMED PAYMENTS ({pendingAlerts.length})
          </Mo>
          {pendingAlerts.map(alert => (
            <div key={alert.id} data-testid={`payment-alert-${alert.id}`} style={{
              padding: '14px 16px', marginBottom: 8,
              background: `${GOLD}08`, border: `1px solid ${GOLD}25`, borderRadius: 12,
            }}>
              <div style={{ marginBottom: 10 }}>
                <Dp style={{ fontSize: 18, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 3 }}>
                  ${alert.amount.toFixed(2)}
                </Dp>
                <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginBottom: 3 }}>
                  {alert.description}
                </Mo>
                <Mo style={{ fontSize: 10, color: 'var(--ghost)', display: 'block' }}>
                  {alert.date} · Amount doesn't match linked signatures
                </Mo>
              </div>
              <Mo style={{ fontSize: 11, color: 'var(--gold)', display: 'block', marginBottom: 10, lineHeight: 1.5 }}>
                Was this a payment to {debt.name}?
              </Mo>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  data-testid={`alert-confirm-${alert.id}`}
                  onClick={() => handleAlertAction(alert.id, 'confirm')}
                  disabled={!!actioning}
                  style={{
                    flex: 1, padding: '10px 0', background: GOLD, border: 'none', borderRadius: 8,
                    fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                    color: '#000', cursor: 'pointer', opacity: actioning === alert.id + 'confirm' ? 0.6 : 1,
                  }}
                >
                  YES, LOG IT
                </button>
                <button
                  data-testid={`alert-dismiss-${alert.id}`}
                  onClick={() => handleAlertAction(alert.id, 'dismiss')}
                  disabled={!!actioning}
                  style={{
                    flex: 1, padding: '10px 0', background: 'transparent', border: `1px solid var(--ghost)`,
                    borderRadius: 8, fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                    color: 'var(--mute)', cursor: 'pointer', opacity: actioning === alert.id + 'dismiss' ? 0.6 : 1,
                  }}
                >
                  NOT MINE
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Payment history — only shown once properly linked */}
      {!showLinkBanner && payments.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="var(--mute)" strokeWidth="1.5" style={{ marginBottom: 16 }}>
            <path d="M19 5H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002 2V7a2 2 0 00-2-2z" />
            <path d="M3 10h18" />
          </svg>
          <Dp data-testid="no-payments-message" style={{ fontSize: 16, fontWeight: 700, color: 'var(--light)', display: 'block', marginBottom: 8 }}>No payments detected yet</Dp>
          <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', lineHeight: 1.6, maxWidth: 280, margin: '0 auto 20px' }}>
            {hasReferenceCode
              ? 'No payments matched your linked account. You can re-link to update the payment source.'
              : 'Payments will appear here automatically once your next payment posts.'}
          </Bd>
          {hasReferenceCode && (
            <button
              data-testid="relink-payment-source-btn"
              onClick={() => setShowLinkModal(true)}
              style={{
                padding: '10px 20px', background: 'transparent',
                border: `1px solid ${GOLD}60`, borderRadius: 8, cursor: 'pointer',
                fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: 1, color: GOLD,
              }}
            >
              RE-LINK PAYMENT SOURCE
            </button>
          )}
        </div>
      )}

      {!showLinkBanner && payments.length > 0 && (() => {
        const total = payments.reduce((sum, p) => sum + p.amount, 0);
        return (
          <>
            {/* Summary */}
            <div style={{ padding: 16, background: `${GOLD}08`, border: `1px solid ${GOLD}20`, borderRadius: 12, marginBottom: 16 }}>
              <div className="dettio-flex dettio-flex--between dettio-flex--center">
                <div>
                  <Mo style={{ fontSize: 10, color: 'var(--gold)', letterSpacing: 1.5, display: 'block', marginBottom: 4 }}>TOTAL DETECTED</Mo>
                  <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)' }}>${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Dp>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginBottom: 4 }}>PAYMENTS</Mo>
                  <Dp style={{ fontSize: 24, fontWeight: 800, color: GOLD }}>{payments.length}</Dp>
                </div>
              </div>
            </div>

            {/* Payment List */}
            <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 12 }}>PAYMENT HISTORY</Mo>
            {payments.map((payment, i) => (
              <div
                key={payment.id || i}
                data-testid={`payment-row-${i}`}
                style={{
                  padding: '14px 0',
                  borderBottom: i < payments.length - 1 ? '1px solid var(--faint)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12
                }}
              >
                <div style={{ width: 36, height: 36, background: `${GOLD}12`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {Ic.check(16, GOLD)}
                </div>
                <div style={{ flex: 1 }}>
                  <Bd style={{ fontSize: 13, fontWeight: 600, color: 'var(--light)' }}>
                    {formatDate(payment.date)}
                  </Bd>
                  <Mo style={{ fontSize: 10, color: 'var(--mute)', display: 'block', marginTop: 2 }}>
                    {payment.description?.length > 30 ? payment.description.slice(0, 30) + '...' : payment.description}
                  </Mo>
                </div>
                <Dp style={{ fontSize: 16, fontWeight: 700, color: '#22c55e' }}>
                  ${payment.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Dp>
              </div>
            ))}
          </>
        );
      })()}

    </div>
  );
}

/* ══════════════════════════════════════
   WHAT-IF TAB - Debt Payoff Simulator
   ══════════════════════════════════════ */
function WhatIfTab({ debt }) {
  const [scenario, setScenario] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [result, setResult] = useState(null);

  const scenarios = [
    { id: 'lump', label: 'Lump Sum Payment', desc: 'What if you made a one-time extra payment?', inputLabel: 'Extra payment amount', inputPrefix: '$' },
    { id: 'extra', label: 'Extra Monthly', desc: 'What if you paid extra each month?', inputLabel: 'Extra monthly amount', inputPrefix: '$' },
    { id: 'refinance', label: 'Refinance', desc: 'What if you got a lower interest rate?', inputLabel: 'New APR', inputSuffix: '%' },
    { id: 'pause', label: 'Pause Payments', desc: 'What happens if you stop paying?', inputLabel: 'Months to pause', inputSuffix: 'mo' },
  ];

  // Calculate results when input changes
  useEffect(() => {
    if (!scenario || !inputValue || !debt) {
      setResult(null);
      return;
    }

    const value = parseFloat(inputValue) || 0;
    if (value <= 0) {
      setResult(null);
      return;
    }

    const balance = debt.balance || 0;
    const apr = (debt.apr || 0) / 100;
    const monthlyRate = apr / 12;
    const minPayment = debt.min_payment || 50;

    let calculatedResult = {};

    switch (scenario) {
      case 'lump': {
        // Lump sum payment - one-time extra payment
        const newBalance = Math.max(0, balance - value);
        const monthsWithLump = calculatePayoffMonths(newBalance, apr, minPayment);
        const monthsWithoutLump = calculatePayoffMonths(balance, apr, minPayment);
        const monthsSaved = Math.max(0, monthsWithoutLump - monthsWithLump);
        
        const interestWithout = calculateTotalInterest(balance, apr, minPayment);
        const interestWith = calculateTotalInterest(newBalance, apr, minPayment);
        const interestSaved = Math.max(0, interestWithout - interestWith);
        
        calculatedResult = {
          primary: { label: 'MONTHS SAVED', value: monthsSaved, color: GOLD },
          secondary: { label: 'INTEREST SAVED', value: `$${Math.round(interestSaved).toLocaleString()}`, color: '#22c55e' },
          description: monthsSaved > 0 
            ? `A $${value.toLocaleString()} payment would shave ${monthsSaved} month${monthsSaved !== 1 ? 's' : ''} off your payoff!`
            : `This payment would help, but your balance is already manageable.`
        };
        break;
      }

      case 'extra': {
        // Extra monthly payment
        const newMonthlyPayment = minPayment + value;
        const monthsWithExtra = calculatePayoffMonths(balance, apr, newMonthlyPayment);
        const monthsWithoutExtra = calculatePayoffMonths(balance, apr, minPayment);
        const monthsSaved = Math.max(0, monthsWithoutExtra - monthsWithExtra);
        
        const interestWithout = calculateTotalInterest(balance, apr, minPayment);
        const interestWith = calculateTotalInterest(balance, apr, newMonthlyPayment);
        const interestSaved = Math.max(0, interestWithout - interestWith);
        
        // Calculate new payoff date
        const payoffDate = new Date();
        payoffDate.setMonth(payoffDate.getMonth() + monthsWithExtra);
        const payoffDateStr = payoffDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        
        calculatedResult = {
          primary: { label: 'NEW PAYOFF DATE', value: payoffDateStr, color: GOLD },
          secondary: { label: 'INTEREST SAVED', value: `$${Math.round(interestSaved).toLocaleString()}`, color: '#22c55e' },
          description: `Paying $${value} extra per month saves $${Math.round(interestSaved).toLocaleString()} in interest and ${monthsSaved} months!`
        };
        break;
      }

      case 'refinance': {
        // Refinance to lower rate
        const newApr = value / 100;
        const oldMonthlyPayment = calculateMonthlyPayment(balance, apr, 60); // Assume 60 month term
        const newMonthlyPayment = calculateMonthlyPayment(balance, newApr, 60);
        const monthlyDiff = oldMonthlyPayment - newMonthlyPayment;
        
        const oldTotalInterest = calculateTotalInterest(balance, apr, minPayment);
        const newTotalInterest = calculateTotalInterest(balance, newApr, minPayment);
        const interestSaved = Math.max(0, oldTotalInterest - newTotalInterest);
        
        calculatedResult = {
          primary: { label: 'MONTHLY SAVINGS', value: `$${Math.round(monthlyDiff).toLocaleString()}`, color: GOLD },
          secondary: { label: 'TOTAL INTEREST SAVED', value: `$${Math.round(interestSaved).toLocaleString()}`, color: '#22c55e' },
          description: monthlyDiff > 0
            ? `Refinancing from ${debt.apr}% to ${value}% saves $${Math.round(monthlyDiff)}/month!`
            : `A ${value}% rate would actually cost more. Current rate is better.`
        };
        break;
      }

      case 'pause': {
        // Pause payments
        const monthsPaused = Math.round(value);
        const accruedInterest = balance * monthlyRate * monthsPaused;
        const newBalance = balance + accruedInterest;
        
        // Calculate new payoff date
        const monthsToPayoff = calculatePayoffMonths(newBalance, apr, minPayment);
        const payoffDate = new Date();
        payoffDate.setMonth(payoffDate.getMonth() + monthsPaused + monthsToPayoff);
        const payoffDateStr = payoffDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        
        calculatedResult = {
          primary: { label: 'INTEREST ACCRUED', value: `$${Math.round(accruedInterest).toLocaleString()}`, color: EMBER },
          secondary: { label: 'NEW PAYOFF DATE', value: payoffDateStr, color: 'var(--mute)' },
          description: `Pausing ${monthsPaused} month${monthsPaused !== 1 ? 's' : ''} adds $${Math.round(accruedInterest).toLocaleString()} in interest.`
        };
        break;
      }

      default:
        calculatedResult = null;
    }

    setResult(calculatedResult);
  }, [scenario, inputValue, debt]);

  const activeScenario = scenarios.find(s => s.id === scenario);

  return (
    <div className="fade-in">
      <Bd style={{ fontSize: 13, color: 'var(--mute)', display: 'block', marginBottom: 16, lineHeight: 1.5 }}>
        Test different strategies before committing. See the exact impact on your debt-free date.
      </Bd>

      {/* Scenario Selection */}
      {scenarios.map(s => (
        <div 
          key={s.id} 
          onClick={() => { setScenario(s.id); setInputValue(''); setResult(null); }}
          data-testid={`scenario-${s.id}`}
          style={{ 
            padding: 16, 
            background: scenario === s.id ? `${GOLD}10` : 'var(--surface)', 
            border: `1px solid ${scenario === s.id ? GOLD : 'var(--faint)'}`, 
            marginBottom: 10, 
            cursor: 'pointer', 
            borderRadius: 12,
            transition: 'all 0.2s'
          }}
        >
          <Bd style={{ fontSize: 14, fontWeight: 600, color: 'var(--light)' }}>{s.label}</Bd>
          <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 4 }}>{s.desc}</Mo>
        </div>
      ))}

      {/* Input Field - appears when scenario selected */}
      {scenario && activeScenario && (
        <div className="fade-in" style={{ marginTop: 16, padding: 16, background: 'var(--surface)', border: '1px solid var(--faint)', borderRadius: 12 }}>
          <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 8 }}>{activeScenario.inputLabel?.toUpperCase()}</Mo>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {activeScenario.inputPrefix && (
              <Mo style={{ fontSize: 18, fontWeight: 700, color: 'var(--mute)' }}>{activeScenario.inputPrefix}</Mo>
            )}
            <input
              type="number"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="0"
              data-testid={`scenario-input-${scenario}`}
              style={{
                flex: 1,
                padding: '12px 14px',
                background: 'var(--faint)',
                border: '1px solid var(--ghost)',
                borderRadius: 8,
                color: 'var(--light)',
                fontFamily: MONO,
                fontSize: 20,
                fontWeight: 700,
                textAlign: 'center',
                outline: 'none',
              }}
            />
            {activeScenario.inputSuffix && (
              <Mo style={{ fontSize: 14, fontWeight: 600, color: 'var(--mute)' }}>{activeScenario.inputSuffix}</Mo>
            )}
          </div>
        </div>
      )}

      {/* Results - appears when we have calculated results */}
      {result && (
        <div className="fade-in" data-testid="scenario-result" style={{ marginTop: 16, padding: 16, background: `${GOLD}06`, border: `1px solid ${GOLD}20`, borderRadius: 12 }}>
          <Mo style={{ fontSize: 10, color: 'var(--gold)', letterSpacing: 1.5, display: 'block', marginBottom: 12 }}>SCENARIO RESULT</Mo>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1, padding: 12, background: 'var(--faint)', textAlign: 'center', borderRadius: 8 }}>
              <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block', marginBottom: 4 }}>{result.primary.label}</Mo>
              <Dp style={{ fontSize: 22, fontWeight: 800, color: result.primary.color }}>{result.primary.value}</Dp>
            </div>
            <div style={{ flex: 1, padding: 12, background: 'var(--faint)', textAlign: 'center', borderRadius: 8 }}>
              <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block', marginBottom: 4 }}>{result.secondary.label}</Mo>
              <Dp style={{ fontSize: 22, fontWeight: 800, color: result.secondary.color }}>{result.secondary.value}</Dp>
            </div>
          </div>
          <Bd style={{ fontSize: 12, color: 'var(--mute)', lineHeight: 1.5 }}>{result.description}</Bd>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   HELPER FUNCTIONS
   ══════════════════════════════════════ */
function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function calculatePayoffMonths(balance, apr, monthlyPayment) {
  if (balance <= 0 || monthlyPayment <= 0) return 0;
  const monthlyRate = apr / 12;
  
  if (monthlyRate === 0) {
    return Math.ceil(balance / monthlyPayment);
  }
  
  // Check if payment covers interest
  const minInterestPayment = balance * monthlyRate;
  if (monthlyPayment <= minInterestPayment) {
    return 999; // Would never pay off
  }
  
  // Standard amortization formula
  const months = Math.log(monthlyPayment / (monthlyPayment - balance * monthlyRate)) / Math.log(1 + monthlyRate);
  return Math.ceil(months);
}

function calculateTotalInterest(balance, apr, monthlyPayment) {
  const months = calculatePayoffMonths(balance, apr, monthlyPayment);
  if (months >= 999) return balance * (apr / 12) * 120; // Cap at 10 years of interest
  return Math.max(0, (months * monthlyPayment) - balance);
}

function calculateMonthlyPayment(balance, apr, months) {
  if (months <= 0) return 0;
  const monthlyRate = apr / 12;
  if (monthlyRate === 0) return balance / months;
  return (balance * monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
}

/* ══════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════ */
export function DebtDetailScreen({ debt: initialDebt, onBack, refreshData }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [debt, setDebt] = useState(initialDebt);
  const [showEdit, setShowEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!debt) return null;

  const handleEditSave = async (form) => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        mask: form.mask || '',
        statement_name: form.statement_name || '',
        type: form.type,
        balance: parseFloat(form.balance) || 0,
        apr: parseFloat(form.apr) || 0,
        min_payment: parseFloat(form.min_payment) || 0,
        due: form.due,
        original: parseFloat(form.original) || 0,
        credit_limit: parseFloat(form.credit_limit) || 0,
      };
      // Recalculate pct_paid
      if (payload.original > 0) {
        payload.pct_paid = Math.round((1 - payload.balance / payload.original) * 100);
      }
      await updateDebt(debt.id, payload);
      setDebt(d => ({ ...d, ...payload }));
      setShowEdit(false);
      if (refreshData) refreshData();
    } catch (err) {
      console.error('Failed to update debt:', err);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteDebt(debt.id);
      if (refreshData) refreshData();
      onBack();
    } catch (err) {
      console.error('Failed to delete debt:', err);
      alert('Failed to remove account. Please try again.');
      setDeleting(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'payments', label: 'Payments' },
    { id: 'whatif', label: 'What-If' },
  ];

  return (
    <div data-testid="debt-detail-screen" className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        <div style={{ height: 'env(safe-area-inset-top, 20px)', minHeight: 20 }} />
        
        {/* Top Bar: Back + Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <button 
            onClick={onBack} 
            data-testid="debt-detail-back-btn"
            style={{
              background: 'var(--faint)', border: 'none', color: 'var(--mute)', fontFamily: MONO,
              fontSize: 11, cursor: 'pointer', padding: '12px 16px', letterSpacing: 0.5,
              display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, borderRadius: 8,
            }}
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            BACK
          </button>

          {/* Edit + Delete buttons */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setShowEdit(true)}
              data-testid="edit-debt-btn"
              style={{
                background: 'var(--faint)', border: `1px solid ${GOLD}40`, color: GOLD,
                fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: 1,
                cursor: 'pointer', padding: '10px 14px', borderRadius: 8,
                display: 'flex', alignItems: 'center', gap: 6, minHeight: 44,
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = `${GOLD}15`}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--faint)'}
            >
              {Ic.edit(14, GOLD)}
              EDIT
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="delete-debt-btn"
              style={{
                background: 'var(--faint)', border: `1px solid ${EMBER}40`, color: EMBER,
                fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: 1,
                cursor: 'pointer', padding: '10px 14px', borderRadius: 8,
                display: 'flex', alignItems: 'center', gap: 6, minHeight: 44,
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = `${EMBER}15`}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--faint)'}
            >
              {Ic.x(14, EMBER)}
              REMOVE
            </button>
          </div>
        </div>

        {/* Header with debt name */}
        <div className="dettio-flex dettio-flex--center dettio-flex--gap-md" style={{ marginBottom: 20 }}>
          {dIc(debt.type)}
          <div>
            <Dp style={{ fontSize: 20, fontWeight: 800, color: 'var(--light)', display: 'block' }}>{debt.name}</Dp>
            <Mo style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 2 }}>
              {debt.type?.charAt(0).toUpperCase() + debt.type?.slice(1)} · {debt.mask ? `····${debt.mask}` : ''}
            </Mo>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'var(--faint)', padding: 4, borderRadius: 10 }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              style={{
                flex: 1, padding: '10px 12px',
                background: activeTab === tab.id ? 'var(--surface)' : 'transparent',
                border: 'none', borderRadius: 8,
                color: activeTab === tab.id ? 'var(--light)' : 'var(--mute)',
                fontFamily: MONO, fontSize: 10, fontWeight: 600, letterSpacing: 0.5,
                cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              {tab.label.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && <OverviewTab debt={debt} />}
        {activeTab === 'payments' && <PaymentsTab debt={debt} />}
        {activeTab === 'whatif' && <WhatIfTab debt={debt} />}
      </div>

      {/* Modals */}
      {showEdit && (
        <EditModal
          debt={debt}
          onSave={handleEditSave}
          onClose={() => setShowEdit(false)}
          saving={saving}
        />
      )}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          debtName={debt.name}
          onConfirm={handleDelete}
          onClose={() => setShowDeleteConfirm(false)}
          deleting={deleting}
        />
      )}
    </div>
  );
}
